// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="TimePicker" set="mytaible-shadcn-studio" verified=tbd
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TimePickerSize = 'sm' | 'md' | 'lg';

/**
 * Props for the TimePickerInput component.
 *
 * Extends all native HTML input attributes except `type` (always `"time"`) and
 * `size` (repurposed as a design-token size variant). Pass `disabled`,
 * `readOnly`, `value`, `onChange`, `id`, `name`, `required`, `aria-*`, etc.
 * directly.
 */
export interface TimePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Field height / text size. Defaults to `'md'`. */
  size?: TimePickerSize;
}

// ─── Size Map ─────────────────────────────────────────────────────────────────

const sizeClasses: Record<TimePickerSize, string> = {
  sm: 'h-7 px-2 text-base sm:text-xs',
  md: 'h-9 px-3 text-base sm:text-sm',
  lg: 'h-10 px-4 text-base',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Styled native `<input type="time">` that matches the visual design of the
 * existing `Input` component.
 *
 * This is a thin wrapper — no Popover, no custom picker. The browser's native
 * time picker is used for input. Forwards its ref to the underlying `<input>`
 * element.
 *
 * Supports `sm`, `md` (default), and `lg` sizes. Error state is driven by
 * `aria-invalid={true}` — set it directly or let `FieldWrapper` / `FormControl`
 * inject it automatically.
 *
 * @example
 * // Basic uncontrolled
 * <TimePickerInput defaultValue="09:00" />
 *
 * @example
 * // Controlled with size
 * <TimePickerInput size="lg" value={time} onChange={(e) => setTime(e.target.value)} />
 *
 * @example
 * // With FieldWrapper
 * <FieldWrapper label="Meeting Time" hint="24-hour format" required>
 *   <TimePickerInput />
 * </FieldWrapper>
 *
 * @example
 * // Error state
 * <TimePickerInput aria-invalid={true} value="14:30" />
 */
const TimePickerInput = React.forwardRef<HTMLInputElement, TimePickerProps>(
  ({ size = 'md', className, ...props }, ref) => (
    <input
      type="time"
      ref={ref}
      className={cn(
        // ── Layout ────────────────────────────────────────────────────────────
        'flex',
        // ── Border radius ─────────────────────────────────────────────────────
        'rounded-[var(--field-radius,var(--radius))]',
        // ── Variant (outline — mirrors Input default) ──────────────────────────
        'border border-input bg-input-bg text-foreground shadow-sm',
        // ── Placeholder ───────────────────────────────────────────────────────
        'placeholder:text-muted-foreground',
        // ── Focus ring ────────────────────────────────────────────────────────
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        // ── Disabled ──────────────────────────────────────────────────────────
        'disabled:cursor-not-allowed disabled:opacity-50',
        // ── ReadOnly ──────────────────────────────────────────────────────────
        'read-only:bg-muted read-only:cursor-default',
        // ── Error — responds to aria-invalid set by FormControl / FieldWrapper ─
        'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
        // ── Size ──────────────────────────────────────────────────────────────
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  ),
);
TimePickerInput.displayName = 'TimePickerInput';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { TimePickerInput };
